import React, { useCallback, useEffect, useState } from 'react';
import {
  socialApi,
  socialPublicationApi,
  SocialPublication,
  SocialConnectionInfo,
  SocialStatus,
  LinkedInMedienKandidat,
} from '../services/api';
import { useConfirm } from '../components/ui';
import { Vorschaubild } from '../components/media/Vorschaubild';
import type { VorschauJeIdee } from '../components/media/useVorschauJeIdee';
import { PLATTFORM_NAME, gepostetVon, kostenText } from './plattformen';
import { useVeroeffentlichungsprofile, profilOption } from './useVeroeffentlichungsprofile';

const STATUS_TEXT: Record<string, string> = {
  draft: 'Entwurf',
  scheduled: 'Geplant',
  approved: 'Freigegeben',
  publishing: 'Wird veröffentlicht',
  published: 'Veröffentlicht',
};
const VEROEFFENTLICHBAR = ['approved', 'scheduled'];
const MEDIEN_ART: Record<string, string> = { image: 'Bild', video: 'Video', pdf: 'PDF-Karussell' };
const NACHLADEN_MS = 10000;

export interface SocialVeroeffentlichungenProps {
  darfFreigeben: boolean;
  /** Vorschaubild je Beitragsidee (vom Kalender schon geladen). */
  vorschauJeIdee?: VorschauJeIdee;
  /** Meldet die geladenen Eintraege (fuer die Monatsansicht). */
  onGeladen?: (eintraege: SocialPublication[]) => void;
}

/**
 * Instagram-, X- und Facebook-Eintraege im Kalender ("Terminieren"):
 * Plattform, Konto (Firma/persoenlich bzw. Seite), Vorschaubild, Status;
 * Freigeben, Veroeffentlichen (X: Kosten bestaetigen), Als veroeffentlicht
 * markieren. Rechte wie bei LinkedIn (linkedin.freigeben).
 */
export const SocialVeroeffentlichungen: React.FC<SocialVeroeffentlichungenProps> = ({ darfFreigeben, vorschauJeIdee, onGeladen }) => {
  const [eintraege, setEintraege] = useState<SocialPublication[]>([]);
  const [status, setStatus] = useState<SocialStatus | null>(null);
  const [fehler, setFehler] = useState('');
  const [erfolg, setErfolg] = useState<{ text: string; link?: string } | null>(null);
  const [laeuftFuer, setLaeuftFuer] = useState<string | null>(null);
  const [medienAuswahl, setMedienAuswahl] = useState<{ eintrag: SocialPublication; kandidaten: LinkedInMedienKandidat[]; gewaehlt: string } | null>(null);
  const [markieren, setMarkieren] = useState<{ eintrag: SocialPublication; link: string } | null>(null);
  const { confirm } = useConfirm();
  // Firmenkonten und persoenliche Profile des Teams ("Mein Profil verbinden").
  const profile = useVeroeffentlichungsprofile();

  const laden = useCallback(async () => {
    try {
      const liste = (await socialPublicationApi.getAll()) ?? [];
      setEintraege(liste);
      onGeladen?.(liste);
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Die Instagram/X/Facebook-Einträge konnten nicht geladen werden.');
    }
  }, [onGeladen]);

  useEffect(() => {
    laden();
    (async () => {
      try {
        setStatus((await socialApi.getStatus()) ?? null);
      } catch {
        setStatus(null);
      }
    })();
  }, [laden]);

  const laeuftNoch = eintraege.some((e) => e.status === 'publishing');
  useEffect(() => {
    if (!laeuftNoch) return undefined;
    const uhr = window.setInterval(() => { laden(); }, NACHLADEN_MS);
    return () => window.clearInterval(uhr);
  }, [laeuftNoch, laden]);

  /** Auswahl je Plattform: Veroeffentlichungsprofile, ohne Liste (altes Backend) die sichtbaren Konten. */
  const konten = (platform: SocialConnectionInfo['platform']): Array<{ id: string; text: string }> => {
    const team = profile.fuer(platform);
    if (team.length > 0) return team.map((p) => ({ id: p.id, text: profilOption(p) }));
    return (status?.connections ?? [])
      .filter((c) => c.platform === platform && c.isActive)
      .map((k) => ({
        id: k.id,
        text: `${k.accountName || k.accountId} (${k.art === 'mandant' ? (platform === 'facebook' ? 'Seite' : 'Firma') : 'persönlich'})`,
      }));
  };

  /** Anzeige des gewaehlten Kontos am Eintrag. */
  const kontoName = (e: SocialPublication): string =>
    profile.finden(e.connectionId)?.anzeigeName || e.connection?.accountName || '–';

  const kontoWechseln = async (eintrag: SocialPublication, connectionId: string) => {
    try {
      await socialPublicationApi.update(eintrag.id, { connectionId: connectionId || null });
      await laden();
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Das Konto konnte nicht geändert werden.');
    }
  };

  const freigeben = async (eintrag: SocialPublication) => {
    try {
      await socialPublicationApi.approve(eintrag.id);
      await laden();
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Fehler beim Freigeben');
    }
  };

  const loeschen = async (eintrag: SocialPublication) => {
    if (!(await confirm({ title: 'Eintrag löschen?', message: 'Der Text bleibt im Generator erhalten.', confirmLabel: 'Löschen', tone: 'danger' }))) return;
    try {
      await socialPublicationApi.delete(eintrag.id);
      await laden();
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const veroeffentlichen = async (eintrag: SocialPublication, mediaAssetId?: string, kostenBestaetigt = false) => {
    if (!darfFreigeben) return;
    const name = PLATTFORM_NAME[eintrag.platform];
    const testmodus = !!status?.plattformen?.[eintrag.platform]?.dryRun;
    // X kostet je Beitrag: vor dem echten Posten die Schaetzung bestaetigen lassen.
    if (eintrag.platform === 'x' && !testmodus && !kostenBestaetigt) {
      const ok = await confirm({
        title: 'Kosten bei X bestätigen',
        message: `Dieser Beitrag kostet bei X voraussichtlich ${kostenText(eintrag.kostenCents)} (Pay-per-use, Schätzung). Jetzt posten?`,
        confirmLabel: 'Kostenpflichtig posten',
      });
      if (!ok) return;
      kostenBestaetigt = true;
    }
    setFehler('');
    setErfolg(null);
    setLaeuftFuer(eintrag.id);
    try {
      const r = await socialPublicationApi.publish(eintrag.id, { mediaAssetId, kostenBestaetigt });
      setMedienAuswahl(null);
      if (r.simulated) {
        const womit = r.medium ? `mit „${r.medium.title}“ (${MEDIEN_ART[r.medium.kind] || r.medium.kind})` : 'als Textbeitrag';
        const kosten = r.kosten ? ` Kosten bei X: ca. ${kostenText(r.kosten.cents)}.` : '';
        const ohneKonto = r.verbunden === false ? ` Hinweis: kein aktives ${name}-Konto gewählt.` : '';
        setErfolg({ text: `Testmodus: nichts gepostet. Der Beitrag würde ${womit} auf ${name} veröffentlicht.${kosten}${ohneKonto}` });
      } else if (r.laeuft) {
        setErfolg({ text: `Wird auf ${name} veröffentlicht. Das Medium wird noch verarbeitet – der Status aktualisiert sich von selbst.` });
      } else {
        setErfolg({ text: `Auf ${name} veröffentlicht.`, link: r.url || r.entry?.url || undefined });
      }
      await laden();
    } catch (err: any) {
      const daten = err?.response?.data;
      if (err?.response?.status === 400 && Array.isArray(daten?.kandidaten) && daten.kandidaten.length > 0) {
        setMedienAuswahl({ eintrag, kandidaten: daten.kandidaten, gewaehlt: daten.kandidaten[0].id });
      } else if (err?.response?.status === 428 && daten?.kosten) {
        const ok = await confirm({
          title: 'Kosten bei X bestätigen',
          message: `Dieser Beitrag kostet bei X voraussichtlich ${kostenText(daten.kosten.cents)}. Jetzt posten?`,
          confirmLabel: 'Kostenpflichtig posten',
        });
        if (ok) await veroeffentlichen(eintrag, mediaAssetId, true);
      } else {
        setFehler(daten?.error || `Fehler beim Veröffentlichen auf ${name}`);
        await laden();
      }
    } finally {
      setLaeuftFuer(null);
    }
  };

  const markierenSpeichern = async () => {
    if (!markieren) return;
    try {
      await socialPublicationApi.markPublished(markieren.eintrag.id, markieren.link.trim() || undefined);
      setMarkieren(null);
      await laden();
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Fehler beim Markieren');
    }
  };

  const vorschauFuer = (e: SocialPublication) => {
    const idee = e.socialPost?.postIdeaId;
    const v = idee ? vorschauJeIdee?.[idee] : null;
    if (e.mediaAssetId) return { assetId: e.mediaAssetId, kind: v?.assetId === e.mediaAssetId ? v.kind : undefined, vorschau: true };
    return v || null;
  };

  return (
    <section aria-label="Instagram, X und Facebook" style={{ marginTop: 'var(--space-6)' }}>
      <h2 className="form-section-title">Instagram, X &amp; Facebook</h2>
      {erfolg && (
        <div className="alert alert-success" role="status">
          {erfolg.text}
          {erfolg.link && <>{' '}<a href={erfolg.link} target="_blank" rel="noopener noreferrer">Beitrag ansehen</a></>}
          <button onClick={() => setErfolg(null)} aria-label="Meldung schließen" style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>x</button>
        </div>
      )}
      {fehler && (
        <div className="alert alert-error">
          {fehler}
          <button onClick={() => setFehler('')} aria-label="Meldung schließen" style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>x</button>
        </div>
      )}
      {darfFreigeben && status && (['instagram', 'x', 'facebook'] as const).some((p) => status.plattformen?.[p]?.dryRun) && (
        <p className="text-sm text-muted">
          Testmodus aktiv für {(['instagram', 'x', 'facebook'] as const).filter((p) => status.plattformen?.[p]?.dryRun).map((p) => PLATTFORM_NAME[p]).join(', ')}:
          „Veröffentlichen“ prüft nur und postet nichts.
        </p>
      )}
      {eintraege.length === 0 ? (
        <p className="text-sm text-secondary">Noch keine Einträge. Im Generator (Reiter Instagram, X oder Facebook) eine Version wählen und einplanen.</p>
      ) : (
        <div className="calendar-list">
          {eintraege.map((e) => {
            const v = vorschauFuer(e);
            return (
              <div key={e.id} className="calendar-list-item" data-testid={`social-eintrag-${e.id}`}>
                <div className="calendar-list-date">
                  <div className="calendar-list-day">{new Date(e.scheduledDate).getDate()}</div>
                  <div className="calendar-list-month">{new Date(e.scheduledDate).toLocaleDateString('de-DE', { month: 'short' })}</div>
                  <div className="calendar-list-time">{e.scheduledTime || ''}</div>
                </div>
                {v && <Vorschaubild assetId={v.assetId} kind={v.kind} vorschau={v.vorschau} groesse="m" alt={`Vorschau zum ${PLATTFORM_NAME[e.platform]}-Beitrag`} />}
                <div className="calendar-list-content">
                  <span className="badge badge-neutral">{PLATTFORM_NAME[e.platform]}</span>{' '}
                  <span className="calendar-list-preview">
                    {e.socialPost ? gepostetVon(e.socialPost).slice(0, 120) : 'Kein Text'}
                  </span>
                  <div className="text-xs text-secondary">
                    {e.platform === 'x' && e.kostenCents != null && <>Kosten ca. {kostenText(e.kostenCents)} · </>}
                    {e.status === 'published' || !darfFreigeben ? (
                      <>{e.platform === 'facebook' ? 'Seite' : 'Konto'}: {kontoName(e)}</>
                    ) : (
                      <label>
                        {e.platform === 'facebook' ? 'Seite' : 'Konto'}:{' '}
                        <select value={e.connectionId || ''} onChange={(ev) => kontoWechseln(e, ev.target.value)} disabled={e.status === 'publishing'}>
                          <option value="">– wählen –</option>
                          {konten(e.platform).map((k) => (
                            <option key={k.id} value={k.id}>{k.text}</option>
                          ))}
                          {e.connectionId && !konten(e.platform).some((k) => k.id === e.connectionId) && (
                            <option value={e.connectionId}>{e.connection?.accountName || 'Konto nicht mehr verbunden'}</option>
                          )}
                        </select>
                      </label>
                    )}
                  </div>
                  {e.publishError && e.status !== 'published' && <div className="text-xs" style={{ color: 'var(--color-danger)' }}>{e.publishError}</div>}
                  {e.url && <a className="text-xs" href={e.url} target="_blank" rel="noopener noreferrer">Beitrag ansehen</a>}
                </div>
                <div className="calendar-list-actions">
                  <span className="badge badge-neutral">{STATUS_TEXT[e.status] || e.status}</span>
                  {e.status === 'scheduled' && darfFreigeben && (
                    <button className="btn btn-success btn-sm" onClick={() => freigeben(e)}>Freigeben</button>
                  )}
                  {VEROEFFENTLICHBAR.includes(e.status) && darfFreigeben && (
                    <>
                      <button className="btn btn-success btn-sm" onClick={() => veroeffentlichen(e)} disabled={laeuftFuer === e.id}>
                        {laeuftFuer === e.id ? 'Wird veröffentlicht …' : `Auf ${PLATTFORM_NAME[e.platform]} veröffentlichen`}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setMarkieren({ eintrag: e, link: e.url || '' })}>Als veröffentlicht markieren</button>
                    </>
                  )}
                  {e.status !== 'publishing' && e.status !== 'published' && (
                    <button className="btn btn-danger btn-sm" onClick={() => loeschen(e)}>Löschen</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {medienAuswahl && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Medium für {PLATTFORM_NAME[medienAuswahl.eintrag.platform]} wählen</h2>
              <button onClick={() => setMedienAuswahl(null)} className="modal-close" aria-label="Schließen">x</button>
            </div>
            <fieldset className="form-group" style={{ border: 'none', padding: 0 }}>
              <legend className="form-label">An der Beitragsidee hängen mehrere Medien</legend>
              {medienAuswahl.kandidaten.map((k) => (
                <label key={k.id} style={{ display: 'block', marginBottom: '6px' }}>
                  <input type="radio" name="social-medium" value={k.id} checked={medienAuswahl.gewaehlt === k.id}
                    onChange={() => setMedienAuswahl({ ...medienAuswahl, gewaehlt: k.id })} />{' '}
                  {k.title} ({MEDIEN_ART[k.kind] || k.kind})
                </label>
              ))}
            </fieldset>
            <div className="btn-group mt-6">
              <button className="btn btn-success" onClick={() => veroeffentlichen(medienAuswahl.eintrag, medienAuswahl.gewaehlt)} disabled={laeuftFuer === medienAuswahl.eintrag.id}>
                Mit diesem Medium veröffentlichen
              </button>
              <button className="btn btn-secondary" onClick={() => setMedienAuswahl(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {markieren && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Als veröffentlicht markieren</h2>
              <button onClick={() => setMarkieren(null)} className="modal-close" aria-label="Schließen">x</button>
            </div>
            <p className="text-sm text-muted">Für Beiträge, die von Hand gepostet wurden. Es wird nichts veröffentlicht.</p>
            <div className="form-group">
              <label className="form-label" htmlFor="social-markieren-link">Link zum Beitrag (optional, https://)</label>
              <input id="social-markieren-link" type="url" className="form-input" value={markieren.link}
                onChange={(ev) => setMarkieren({ ...markieren, link: ev.target.value })} />
            </div>
            <div className="btn-group mt-6">
              <button className="btn btn-success" onClick={markierenSpeichern}>Als veröffentlicht markieren</button>
              <button className="btn btn-secondary" onClick={() => setMarkieren(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SocialVeroeffentlichungen;
